import { Tabs, TabsContent } from '@/components/ui-kit/tabs';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { RefreshButton } from '@/components/core';
import { useCurrentOrgId } from '@/store/use-my-oganization-store';
import { useDebounce } from '@/hooks/use-debounce';
import { useGetOrgContractDocuments } from '@/graphql';
import { useContractArchiveActions } from '../hooks/use-contract-archive-actions';
import { OrgContractDocumentItem } from '../types/contract-document.types';
import CommonContractHeader from '../components/common-contracts-header';
import ContractsTable, { type ContractsTableColumn } from '../components/contracts-table';
import ContractsDataCardView from '../components/contracts-data-card-view';
import { ContractsTabsList, type ContractsTabsListOption } from '../components/contracts-tabs-list';
import DocumentRowActions from '../components/document-row-actions';
import DocumentStatusBadge from '../components/document-status-badge';
import DraftProgressCell from '../components/draft-progress-cell';
import {
  mapItemToRow,
  PAGE_SIZE,
  type ContractDocumentRow,
} from '../constants/mock-contract-documents';
import { isFixedDocxContract } from '../utils';

type ContractsTab = 'all' | 'draft' | 'completed' | 'declined' | 'cancelled' | 'pending';

const TAB_OPTIONS: ReadonlyArray<ContractsTabsListOption<ContractsTab>> = [
  { value: 'all', labelKey: 'ORGCONTRACTS_TABS_ALLDOCUMENTS' },
  { value: 'draft', labelKey: 'ORGCONTRACTS_TABS_DRAFT' },
  { value: 'completed', labelKey: 'ORGCONTRACTS_TABS_COMPLETED' },
  { value: 'declined', labelKey: 'ORGCONTRACTS_TABS_DECLINED' },
  { value: 'cancelled', labelKey: 'ORGCONTRACTS_TABS_CANCELLED' },
  { value: 'pending', labelKey: 'ORGCONTRACTS_TABS_PENDING' },
];

const STATUS_FILTER_BY_TAB: Record<Exclude<ContractsTab, 'all'>, string[]> = {
  draft: ['draft'],
  completed: ['document_completed'],
  declined: ['document_declined'],
  cancelled: ['document_cancelled'],
  pending: [
    'preparation_success',
    'rollout_success',
    'signatory_signed_success',
    'signatory_signed_failed',
    'preparation_failed',
    'rollout_failed',
  ],
};

const HEADER_BY_TAB: Record<ContractsTab, { title: string; subtitle: string }> = {
  all: {
    title: 'ORGCONTRACTS_HEADER_DOCUMENT_STATUS',
    subtitle: 'ORGCONTRACTS_HEADER_DOCUMENT_STATUS_SUBTITLE',
  },
  draft: {
    title: 'ORGCONTRACTS_HEADER_MANAGE_TEMPLATES',
    subtitle: 'ORGCONTRACTS_HEADER_MANAGE_TEMPLATES_SUBTITLE',
  },
  completed: {
    title: 'ORGCONTRACTS_HEADER_DOCUMENT_STATUS',
    subtitle: 'ORGCONTRACTS_HEADER_DOCUMENT_STATUS_SUBTITLE',
  },
  declined: {
    title: 'ORGCONTRACTS_HEADER_DOCUMENT_STATUS',
    subtitle: 'ORGCONTRACTS_HEADER_DOCUMENT_STATUS_SUBTITLE',
  },
  cancelled: {
    title: 'ORGCONTRACTS_HEADER_DOCUMENT_STATUS',
    subtitle: 'ORGCONTRACTS_HEADER_DOCUMENT_STATUS_SUBTITLE',
  },
  pending: {
    title: 'ORGCONTRACTS_HEADER_DOCUMENT_STATUS',
    subtitle: 'ORGCONTRACTS_HEADER_DOCUMENT_STATUS_SUBTITLE',
  },
};

const OrgContractsPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const orgId = useCurrentOrgId();
  const [activeTab, setActiveTab] = useState<ContractsTab>('all');
  const [search, setSearch] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const debouncedSearch = useDebounce(search, 500);

  const queryParams = useMemo(
    () => ({
      paging: { pageNo: currentPage, pageSize: PAGE_SIZE },
      where: {
        ...(activeTab === 'all' ? {} : { Status: { in: STATUS_FILTER_BY_TAB[activeTab] } }),
        ...(orgId ? { OrganizationId: { eq: orgId } } : {}),
        or: [{ IsInTrash: { eq: false } }, { IsInTrash: { eq: null } }],
        IsArchived: { eq: false },
        ...(debouncedSearch.trim()
          ? { TemplateTitle: { contains: debouncedSearch.trim() } }
          : {}),
      },
    }),
    [activeTab, orgId, debouncedSearch, currentPage]
  );




  const { data: documentsResult, isLoading: isDocumentsLoading } =
    useGetOrgContractDocuments(queryParams);
  const data = documentsResult?.getOrgContractDocuments;
  const { isUpdating, archiveDocument, moveToTrashDocument } = useContractArchiveActions();
  const documents = useMemo(() => {
    if (!data?.items?.length) return [];

    return data.items.map((item) => mapItemToRow(item as OrgContractDocumentItem));
  }, [data]);

  useEffect(() => {
    setCurrentPage(1);
  }, [activeTab, search]);

  const total = data?.totalCount ?? 0;
  const totalPages = data?.totalCount ? Math.max(1, Math.ceil(data.totalCount / PAGE_SIZE)) : 1;
  const safePage = Math.min(currentPage, totalPages);
  const startItem = total === 0 ? 0 : (safePage - 1) * PAGE_SIZE + 1;
  const endItem = total === 0 ? 0 : Math.min(safePage * PAGE_SIZE, total);

  const isEditableStatus = (status: ContractDocumentRow['status']) =>
    status === 'draft' || status === 'generation_completed';

  const handleRowClick = (row: ContractDocumentRow) => {
    if (!row.templateSlug) return;
    // Fixed-DOCX contracts have no editable LegalDocument — open the DOCX review
    // viewer instead of the classic generate flow.
    if (isFixedDocxContract(row.documentRaw)) {
      navigate(`/templates/${row.templateSlug}/review?document=${row.id}`);
      return;
    }
    if (isEditableStatus(row.status)) {
      navigate(`/templates/${row.templateSlug}/generate?document=${row.id}&fromEdit=true`);
    }
  };

  const handleArchive = async (row: ContractDocumentRow) => {
    await archiveDocument(row.id);
  };

  const handleMoveToTrash = async (row: ContractDocumentRow) => {
    await moveToTrashDocument(row.id);
  };

  const columns = useMemo<ContractsTableColumn<ContractDocumentRow>[]>(() => {
    const titleColumn: ContractsTableColumn<ContractDocumentRow> = {
      id: 'title',
      header: t('ORGCONTRACTS_COLUMNS_TITLE'),
      cell: (row) => (
        <div className="space-y-1 cursor-pointer" onClick={() => handleRowClick(row)}>
          <p className="max-w-90 truncate text-[15px] font-semibold leading-5 text-[hsl(var(--fg))]">
            {row.title}
          </p>
          <p className="text-sm text-globe-grey">{row.recipientsSummary ?? '-'}</p>
        </div>
      ),
    };

    const actionColumn: ContractsTableColumn<ContractDocumentRow> = {
      id: 'action',
      header: t('ORGCONTRACTS_COLUMNS_ACTION'),
      headerClassName: 'text-right',
      cellClassName: 'text-right',
      cell: (row) => (
        <DocumentRowActions
          slug={row.templateSlug as string}
          status={row.status}
          isLoading={isUpdating}
          onArchive={() => handleArchive(row)}
          onMoveToTrash={() => handleMoveToTrash(row)}
          onResume={
            isEditableStatus(row.status) && row.templateSlug ? () => handleRowClick(row) : undefined
          }
          documentId={row.id}
          currentTitle={row.title}
          templateTitle={row.templateTitle}
          documentRaw={row.documentRaw}
          formDataRaw={row.formDataRaw}
          signatoriesRaw={row.signatoriesRaw}
          sigDocumentUrl={row.sigDocumentUrl}
          sigDocumentId={row.sigDocumentId}
        />
      ),
    };

    if (activeTab === 'draft') {
      return [
        titleColumn,
        {
          id: 'lastModified',
          header: t('ORGCONTRACTS_COLUMNS_LASTMODIFIED'),
          cell: (row) => (
            <p className="text-[15px] font-semibold text-[hsl(var(--fg))]">
              {row.lastEditedAt ?? '-'}
            </p>
          ),
        },
        {
          id: 'processingPercentage',
          header: t('ORGCONTRACTS_COLUMNS_PROCESSINGPERCENTAGE'),
          cell: (row) => <DraftProgressCell progressPercent={row.progressPercent} />,
        },
        actionColumn,
      ];
    }

    return [
      titleColumn,
      {
        id: 'createdDate',
        header: t('ORGCONTRACTS_COLUMNS_CREATEDDATE'),
        cell: (row) => (
          <div className="space-y-1">
            <p className="text-[15px] font-semibold text-[hsl(var(--fg))]">
              {row.createdAt ?? '-'}
            </p>
            {row.lastUpdatedDate ? (
              <p className="text-xs text-muted-foreground">
                {t('ORGCONTRACTS_LAST_UPDATED_ON', { date: row.lastUpdatedDate })}
              </p>
            ) : null}
          </div>
        ),
      },
      {
        id: 'status',
        header: t('ORGCONTRACTS_COLUMNS_STATUS'),
        cell: (row) => <DocumentStatusBadge status={row.status} />,
      },
      actionColumn,
    ];
  }, [activeTab, isUpdating, t]); // eslint-disable-line react-hooks/exhaustive-deps

  const header = HEADER_BY_TAB[activeTab];

  return (
    <div className="space-y-6 bg-white font-nunito">
      <CommonContractHeader
        title={header.title}
        subtitle={header.subtitle}
        search={search}
        setSearch={setSearch}
        searchPlaceholder="ORGCONTRACTS_SEARCHPLACEHOLDER"
        // Mobile-only refresh trigger sits beside the search input. The
        // desktop refresh button still lives in the tabs row below.
        action={
          <RefreshButton
            queryKey={['contractDocuments']}
            label={t('ORGTEAM_HEADER_REFRESH')}
            className="md:hidden h-11"
          />
        }
      />

      <Tabs value={activeTab} onValueChange={(value) => setActiveTab(value as ContractsTab)}>
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <ContractsTabsList
            options={TAB_OPTIONS}
            activeValue={activeTab}
            onSelect={setActiveTab}
          />

          <RefreshButton
            queryKey={['contractDocuments']}
            label={t('ORGTEAM_HEADER_REFRESH')}
            className="hidden md:inline-flex sm:self-start"
          />
        </div>

        <TabsContent value={activeTab} className="mt-4">
          {/* Desktop: classic data table */}
          <div className="hidden md:block">
            <ContractsTable
              columns={columns}
              data={documents}
              rowKey={(row) => row.id}
              emptyMessage={t('ORGCONTRACTS_EMPTY')}
              isLoading={isDocumentsLoading}
              loadingRowCount={PAGE_SIZE}
              pagination={{
                page: safePage,
                limit: PAGE_SIZE,
                total,
                totalPages,
              }}
              onPageChange={(page) => setCurrentPage(Math.min(Math.max(page, 1), totalPages))}
              showingLabel={t('ORGCONTRACTS_SHOWING_RANGE', {
                start: String(startItem).padStart(2, '0'),
                end: String(endItem).padStart(2, '0'),
                total,
              })}
            />
          </div>

          {/* Mobile: card layout sharing the same handlers/components */}
          <div className="md:hidden">
            <ContractsDataCardView
              documents={documents}
              isLoading={isDocumentsLoading}
              loadingRowCount={PAGE_SIZE}
              emptyMessage={t('ORGCONTRACTS_EMPTY')}
              isDraftTab={activeTab === 'draft'}
              isUpdating={isUpdating}
              isEditableStatus={isEditableStatus}
              onRowClick={handleRowClick}
              onArchive={handleArchive}
              onMoveToTrash={handleMoveToTrash}
              pagination={{
                page: safePage,
                limit: PAGE_SIZE,
                total,
                totalPages,
              }}
              onPageChange={(page) => setCurrentPage(Math.min(Math.max(page, 1), totalPages))}
              showingLabel={t('ORGCONTRACTS_SHOWING_RANGE', {
                start: String(startItem).padStart(2, '0'),
                end: String(endItem).padStart(2, '0'),
                total,
              })}
            />
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default OrgContractsPage;
